import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Button, Modal, Box, Typography } from '@mui/material';
import { Close, School, Login } from '@mui/icons-material';
import { styled as muiStyled } from '@mui/material/styles';
import { map } from 'lodash';
import { handleListDistrictAuth } from 'store/canvas/thunks';
import { useCanvasDistrictAuth } from 'store/canvas/selectors';
import { authenticateWithCanvas } from 'components/auth_flow/handleCanvasAuth';
import Loading from 'components/Loading';
import styled from 'styled-components';
import COLOR from 'utils/constants/color';

const local_store_keys = {
    canvas_selected_district: "canvas_selected_district"
};

const StyledModal = muiStyled(Modal)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '& .MuiBackdrop-root': {
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
    },
}));

const ModalContent = styled(Box)`
    background: ${COLOR.white};
    border-radius: 16px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
    width: 90%;
    max-width: 500px;
    max-height: 80vh;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    position: relative;
`;

const ModalHeader = styled.div`
    background: linear-gradient(135deg, ${COLOR.green100} 0%, ${COLOR.green700} 100%);
    padding: 24px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    color: ${COLOR.white};
`;

const HeaderContent = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
`;

const ModalTitle = styled(Typography)`
    font-size: 20px;
    font-weight: 600;
    color: ${COLOR.white};
    margin: 0;
`;

const CloseButton = styled.button`
    background: rgba(255, 255, 255, 0.2);
    border: none;
    border-radius: 50%;
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: ${COLOR.white};
    transition: all 0.2s ease;
    
    &:hover {
        background: rgba(255, 255, 255, 0.3);
        transform: scale(1.05);
    }
    
    &:active {
        transform: scale(0.95);
    }
`;

const ModalBody = styled.div`
    padding: 24px;
    overflow-y: auto;
    flex: 1;
`;

const Description = styled(Typography)`
    color: ${COLOR.black500};
    font-size: 14px;
    margin-bottom: 20px;
    text-align: center;
    line-height: 1.5;
`;

const DistrictList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const DistrictButton = styled(Button)`
    width: 100%;
    padding: 16px 20px;
    border-radius: 12px;
    text-transform: none;
    font-size: 15px;
    font-weight: 500;
    background: ${COLOR.white};
    border: 2px solid ${COLOR.black800};
    color: ${COLOR.black200};
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 12px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    
    &:hover {
        background: linear-gradient(135deg, rgba(3, 152, 158, 0.1) 0%, rgba(3, 152, 158, 0.1) 100%);
        border-color: ${COLOR.green100};
        color: ${COLOR.green100};
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(3, 152, 158, 0.2);
    }
    
    &:active {
        transform: translateY(0);
    }
`;

const SchoolIcon = styled(School)`
    font-size: 24px;
`;

const EmptyState = styled.div`
    text-align: center;
    padding: 40px 20px;
    color: ${COLOR.black500};
`;

const EmptyStateText = styled(Typography)`
    color: ${COLOR.black500};
    font-size: 14px;
`;

const LoginButton = styled(Button)`
    width: 100% !important;
    min-height: 42px !important;
    height: 42px !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    gap: 8px;
    padding: 8px 16px !important;
    box-sizing: border-box !important;
`;

const CanvasLoginButton = () => {
    const dispatch = useDispatch();
    const [canvasDistrictLoader, setCanvasDistrictLoader] = useState(false);
    const [canvasDistModal, setCanvasDistrictModal] = useState(false);
    const canvasAuthDistrict = useCanvasDistrictAuth();

    const fetchCanvasDistrict = async () => {
        setCanvasDistrictLoader(true);
        await dispatch(handleListDistrictAuth());
        setCanvasDistrictModal(true);
        setCanvasDistrictLoader(false);
    };

    const districts = canvasAuthDistrict || [];
    const hasDistricts = districts.length > 0;

    return (
        <>
            <StyledModal
                open={canvasDistModal}
                onClose={() => {
                    setCanvasDistrictModal(false);
                }}
            >
                <ModalContent>
                    <ModalHeader>
                        <HeaderContent>
                            <SchoolIcon />
                            <ModalTitle>Select Your District</ModalTitle>
                        </HeaderContent>
                        <CloseButton onClick={() => setCanvasDistrictModal(false)}>
                            <Close />
                        </CloseButton>
                    </ModalHeader>
                    <ModalBody>
                        <Description>
                            Please select your district to complete your Canvas authentication.
                        </Description>
                        {hasDistricts ? (
                            <DistrictList>
                                {map(districts, district => (
                                    <DistrictButton
                                        key={district.id}
                                        onClick={() => {
                                            localStorage.setItem(local_store_keys.canvas_selected_district, district.id);
                                            authenticateWithCanvas(district.base_url, district.key, district.redirect_uri);
                                        }}
                                    >
                                        <SchoolIcon />
                                        {district.display_name}
                                    </DistrictButton>
                                ))}
                            </DistrictList>
                        ) : (
                            <EmptyState>
                                <EmptyStateText>
                                    No districts available at the moment. Please try again later.
                                </EmptyStateText>
                            </EmptyState>
                        )}
                    </ModalBody>
                </ModalContent>
            </StyledModal>
            <LoginButton
                disabled={canvasDistrictLoader}
                variant="outlined"
                color="primary"
                onClick={() => fetchCanvasDistrict()}
                type='button'
            >
                {canvasDistrictLoader ? (
                    <Loading size={14} />
                ) : (
                    <Login style={{ fontSize: 18 }} />
                )}
                <span>Login with Canvas</span>
            </LoginButton>
        </>
    );
};

export default CanvasLoginButton;
